/**
 * Find Page - Mobile Responsive
 * Browse and filter all accounts by type, location, genre, etc.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';
import { geocodeCityState } from '../utils/geocoding';
import Navbar from '../components/Navbar';
import SearchInput from '../components/SearchInput';
import '../styles/colors.css';

const GENRE_LIST = [
  'Ambient', 'Alternative', 'Blues', 'Classical', 'Country', 'Cultural',
  'Dance', 'EDM', 'Electronic', 'Emo', 'Experimental', 'Folk', 'Grunge',
  'Hardcore', 'Hip-hop', 'Instrumental', 'Jazz', 'Metal', 'Noise', 'Pop',
  'Psychedelic', 'Punk', 'R&B', 'Rap', 'Reggae-Ska', 'Religious', 'Rock',
  'Shoegaze', 'Singer-Songwriter', 'Spoken Word'
];

const VENUE_TYPES = ['bar', 'restaurant', 'club', 'diy', 'theater', 'house', 'other'];

const ACCOUNT_TABS = [
  { label: 'All',                value: 'all' },
  { label: 'Bands/Artists',      value: 'band' },
  { label: 'Promoters',          value: 'promoter' },
  { label: 'Venues',             value: 'venue' },
  { label: 'Photographers',      value: 'photographer' },
  { label: 'Vendors',            value: 'vendor' },
  { label: 'Recording Studios',  value: 'studio' },
  { label: 'Fans',               value: 'fan' },
];

const getCardSubtitle = (user) => {
  const parts = [];
  if (user.city && user.state) parts.push(`${user.city}, ${user.state}`);
  if (user.account_role === 'band' && user.genre_tags?.length > 0) {
    parts.push(user.genre_tags.slice(0, 3).join(' · '));
  }
  if (user.account_role === 'venue' && user.venue_type?.length > 0) {
    parts.push(user.venue_type.map(t => t.charAt(0).toUpperCase() + t.slice(1)).join(', '));
  }
  if (['band', 'photographer', 'vendor', 'promoter'].includes(user.account_role) && user.travel_distance) {
    parts.push(`${user.travel_distance} mi radius`);
  }
  return parts.join(' · ');
};

const FindPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState(() => {
    const saved = sessionStorage.getItem('findPage_filters');
    return saved ? JSON.parse(saved).searchQuery || '' : '';
  });
  const [activeTab, setActiveTab] = useState(() => {
    const saved = sessionStorage.getItem('findPage_filters');
    return saved ? JSON.parse(saved).activeTab || 'all' : 'all';
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedGenres, setSelectedGenres] = useState(() => {
    const saved = sessionStorage.getItem('findPage_filters');
    return saved ? JSON.parse(saved).selectedGenres || [] : [];
  });
  const [selectedVenueTypes, setSelectedVenueTypes] = useState(() => {
    const saved = sessionStorage.getItem('findPage_filters');
    return saved ? JSON.parse(saved).selectedVenueTypes || [] : [];
  });
  const [selectedAges, setSelectedAges] = useState(() => {
    const saved = sessionStorage.getItem('findPage_filters');
    return saved ? JSON.parse(saved).selectedAges || [] : [];
  });
  const [locationCity, setLocationCity] = useState(() => {
    const saved = sessionStorage.getItem('findPage_filters');
    if (saved) return JSON.parse(saved).locationCity || '';
    return user?.city || '';
  });
  const [locationState, setLocationState] = useState(() => {
    const saved = sessionStorage.getItem('findPage_filters');
    if (saved) return JSON.parse(saved).locationState || '';
    return user?.state || '';
  });
  const [filterRadius, setFilterRadius] = useState(() => {
    const saved = sessionStorage.getItem('findPage_filters');
    if (saved) return JSON.parse(saved).filterRadius || '';
    return user?.travel_distance ? String(user.travel_distance) : '';
  });
  const [sortBy, setSortBy] = useState(() => {
    const saved = sessionStorage.getItem('findPage_filters');
    return saved ? JSON.parse(saved).sortBy || 'distance' : 'distance';
  });
  const [locationError, setLocationError] = useState('');
  const [locationLoading, setLocationLoading] = useState(false);
  const [filterLat, setFilterLat] = useState(() => {
    const saved = sessionStorage.getItem('findPage_filters');
    return saved ? JSON.parse(saved).filterLat || null : null;
  });
  const [filterLon, setFilterLon] = useState(() => {
    const saved = sessionStorage.getItem('findPage_filters');
    return saved ? JSON.parse(saved).filterLon || null : null;
  });

  useEffect(() => {
    const autoApplyUserLocation = async () => {
      if (filterLat && filterLon && filterRadius) {
        fetchUsers(activeTab, filterLat, filterLon);
      } else if (locationCity && locationState && filterRadius && !filterLat && !filterLon) {
        try {
          const result = await geocodeCityState(locationCity, locationState);
          setFilterLat(result.latitude);
          setFilterLon(result.longitude);
          await fetchUsers(activeTab, result.latitude, result.longitude);
        } catch (err) {
          console.error('Failed to auto-geocode user location:', err);
          fetchUsers(activeTab, null, null);
        }
      } else {
        fetchUsers(activeTab, null, null);
      }
    };
    autoApplyUserLocation();
  }, []);

  useEffect(() => {
    const filters = {
      activeTab, searchQuery, selectedGenres, selectedVenueTypes, selectedAges,
      locationCity, locationState, filterRadius, sortBy, filterLat, filterLon
    };
    sessionStorage.setItem('findPage_filters', JSON.stringify(filters));
  }, [activeTab, searchQuery, selectedGenres, selectedVenueTypes, selectedAges, 
      locationCity, locationState, filterRadius, sortBy, filterLat, filterLon]);

  const buildQueryParams = (tab, lat, lon) => {
    const params = new URLSearchParams();
    if (tab && tab !== 'all') params.append('accountRole', tab);
    if (tab === 'band' && selectedGenres.length > 0) params.append('genres', selectedGenres.join(','));
    if (tab === 'venue' && selectedVenueTypes.length > 0) params.append('venueType', selectedVenueTypes.join(','));
    if (tab === 'venue' && selectedAges.length > 0) params.append('ageRestriction', selectedAges.join(','));
    if (sortBy) params.append('sortBy', sortBy);
    if (lat && lon && filterRadius) {
      params.append('filterLat', lat);
      params.append('filterLon', lon);
      params.append('filterRadius', filterRadius);
    }
    return params.toString();
  };

  const fetchUsers = async (tab, lat, lon) => {
    setLoading(true);
    setError('');
    try {
      const qs = buildQueryParams(tab, lat, lon);
      const response = await apiClient.get(qs ? `/users?${qs}` : '/users');
      if (response.data.success) setUsers(response.data.users);
    } catch (err) {
      setError('Failed to load accounts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tabValue) => {
    if (tabValue !== 'band') setSelectedGenres([]);
    if (tabValue !== 'venue') { setSelectedVenueTypes([]); setSelectedAges([]); }
    setActiveTab(tabValue);
    fetchUsers(tabValue, filterLat, filterLon);
  };

  const handleGenreToggle = (g) => setSelectedGenres(prev => prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g]);
  const handleVenueTypeToggle = (t) => setSelectedVenueTypes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  const handleAgeToggle = (a) => setSelectedAges(prev => prev.includes(a) ? prev.filter(x => x !== a) : [...prev, a]);

  const handleApplyFilters = async () => {
    setLocationError('');
    if (locationCity && locationState) {
      if (!filterRadius) { setLocationError('Please enter a distance radius when filtering by location.'); return; }
      setLocationLoading(true);
      try {
        const result = await geocodeCityState(locationCity, locationState);
        setFilterLat(result.latitude);
        setFilterLon(result.longitude);
        await fetchUsers(activeTab, result.latitude, result.longitude);
      } catch (err) {
        setLocationError(err.message);
        setFilterLat(null); setFilterLon(null);
      } finally {
        setLocationLoading(false);
      }
    } else {
      setFilterLat(null); setFilterLon(null);
      await fetchUsers(activeTab, null, null);
    }
  };

  const handleLocationBlur = async () => {
    if (locationCity && locationState && filterRadius) {
      if (locationCity.trim().length >= 2 && locationState.length === 2 && parseInt(filterRadius) > 0) {
        await handleApplyFilters();
      }
    } else if (!locationCity && !locationState && !filterRadius) {
      setFilterLat(null);
      setFilterLon(null);
      setLocationError('');
      await fetchUsers(activeTab, null, null);
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedGenres([]); setSelectedVenueTypes([]); setSelectedAges([]);
    setLocationCity(''); setLocationState(''); setFilterRadius('');
    setSortBy('distance'); setFilterLat(null); setFilterLon(null); setLocationError('');
    fetchUsers(activeTab, null, null);
  };

  const activeFilterCount = () => {
    let n = 0;
    if (selectedGenres.length > 0) n++;
    if (selectedVenueTypes.length > 0) n++;
    if (selectedAges.length > 0) n++;
    if (sortBy !== 'distance') n++;
    if (searchQuery.trim()) n++;
    return n;
  };

  const filteredUsers = users.filter(user => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const displayName = (user.display_name || '').toLowerCase();
    return displayName.includes(query);
  });

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    if (sortBy === 'alphabetical') {
      return (a.display_name || '').localeCompare(b.display_name || '');
    }
    return 0;
  });

  return (
    <>
      <style>{`
        .find-container {
          max-width: 1100px;
          margin: 0 auto;
          padding: 140px 20px 60px;
        }

        .find-title {
          text-align: center;
          font-size: 2.6rem;
          margin-bottom: 30px;
          color: var(--text-light);
        }

        .tabs-row {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          justify-content: center;
          margin-bottom: 28px;
        }

        .location-bar {
          background-color: var(--smoke-gray);
          padding: 12px 24px;
          border-radius: 8px;
          display: flex;
          gap: 12px;
          align-items: center;
          flex-wrap: wrap;
          margin-bottom: 20px;
        }

        .location-bar input:first-of-type {
          max-width: 200px;
        }

        .search-row {
          margin-bottom: 20px;
          display: flex;
          gap: 16px;
          justify-content: center;
          flex-wrap: wrap;
        }

        .filter-row {
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .results-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
          gap: 20px;
          margin-top: 20px;
        }

        .user-card-subtitle {
          font-size: 13px;
          color: #aaa;
          line-height: 1.4;
        }

        @media (max-width: 768px) {
          .find-container {
            padding: 125px 16px 60px 16px;
          }

          .find-title {
            font-size: 1.8rem;
            margin-bottom: 20px;
          }

          .tabs-row {
            overflow-x: auto;
            flex-wrap: nowrap;
            justify-content: flex-start;
            margin-bottom: 0;
            padding-bottom: 16px;
            gap: 12px;
            scroll-snap-type: x mandatory;
            -webkit-overflow-scrolling: touch;
            margin-left: -16px;
            margin-right: -16px;
            padding-left: 16px;
            padding-right: 16px;
          }

          .tabs-row::-webkit-scrollbar {
            display: none;
          }

          .tabs-row button {
            flex-shrink: 0;
            scroll-snap-align: start;
            font-size: 15px !important;
            padding: 12px 20px !important;
          }

          .location-bar {
            padding: 12px 12px 12px 0;
            margin-bottom: 16px;
            border-radius: 0;
            flex-direction: column;
            align-items: stretch;
            gap: 8px;
            width: fit-content;
            max-width: 90vw;
          }

          .location-inputs {
            display: flex;
            gap: 8px;
          }

          .location-bar input {
            font-size: 16px !important;
            padding: 12px !important;
          }

          .search-row {
            flex-direction: column;
            margin-bottom: 16px;
            gap: 0;
            max-width: 85vw;
            padding-right: 16px;
          }

          .search-row input {
            font-size: 16px !important;
            padding: 12px !important;
            width: 100%;
          }

          .filter-row {
            flex-wrap: wrap;
          }

          .filter-row button {
            font-size: 15px !important;
            padding: 12px 20px !important;
          }

          .results-grid {
            grid-template-columns: 1fr;
            gap: 16px;
          }

          .user-card-name {
            font-size: 17px !important;
          }

          .user-card-subtitle {
            font-size: 14px !important;
            color: #aaa !important;
          }

          .filter-panel {
            padding: 20px !important;
            margin-bottom: 20px !important;
          }

          .filter-checkboxes label {
            font-size: 15px;
          }

          .filter-checkboxes input[type="checkbox"] {
            width: 20px;
            height: 20px;
            cursor: pointer;
          }
        }
      `}</style>

      <div className="find-container">
        <Navbar />

        <h1 className="find-title">Find</h1>

        <div className="tabs-row">
          {ACCOUNT_TABS.map(tab => {
            const isActive = activeTab === tab.value;
            return (
              <button
                key={tab.value}
                onClick={() => handleTabChange(tab.value)}
                className={isActive ? 'tab tab-dark-selected' : 'tab tab-dark-unselected'}
                style={{ padding: '10px 24px', borderRadius: '30px', fontSize: '14px' }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="search-row">
          <SearchInput
            placeholder="Search by name..."
            onSearch={setSearchQuery}
            initialValue={searchQuery}
            style={{ flex: 1, minWidth: '250px' }}
          />
        </div>

        <div className="location-bar">
          <div className="location-inputs" style={{ flex: 1, display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="City"
              value={locationCity}
              onChange={(e) => setLocationCity(e.target.value)}
              onBlur={handleLocationBlur}
              style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid var(--border-light)', fontSize: '14px', flex: 1, minWidth: '60px', maxWidth: '180px' }}
            />
            <input
              type="text"
              placeholder="ST"
              value={locationState}
              onChange={(e) => setLocationState(e.target.value.toUpperCase())}
              onBlur={handleLocationBlur}
              maxLength={2}
              style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid var(--border-light)', fontSize: '14px', width: '47px' }}
            />
            <input
              type="number"
              placeholder="Radius (mi)"
              value={filterRadius}
              onChange={(e) => setFilterRadius(e.target.value)}
              onBlur={handleLocationBlur}
              style={{ padding: '8px 12px', borderRadius: '4px', border: '1px solid var(--border-light)', fontSize: '14px', width: '53px' }}
            />
          </div>
          {locationError && <div style={{ color: 'var(--accent-danger)', fontSize: '13px', width: '100%' }}>{locationError}</div>}
          {locationLoading && <div style={{ color: 'var(--text-muted)', fontSize: '13px', width: '100%' }}>Locating...</div>}
        </div>

        <div className="filter-row">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn btn-primary"
            style={{ padding: '9px 20px', fontWeight: activeFilterCount() > 0 ? '700' : '600', opacity: activeFilterCount() > 0 ? 1 : 0.8 }}
          >
            {showFilters ? 'Hide Filters' : `Filter Options${activeFilterCount() > 0 ? ` (${activeFilterCount()})` : ''}`}
          </button>
          {activeFilterCount() > 0 && (
            <button onClick={handleClearFilters} className="btn btn-danger" style={{ padding: '9px 20px' }}>
              Clear All
            </button>
          )}
        </div>

        {showFilters && (
          <div className="card filter-panel" style={{ padding: '20px', marginBottom: '24px' }}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-dark)', display: 'block', marginBottom: '8px' }}>Sort by</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{ padding: '10px 14px', borderRadius: '4px', border: '1px solid var(--border-light)', fontSize: '15px', backgroundColor: 'var(--bg-card)', color: 'var(--text-dark)', cursor: 'pointer', width: '100%' }}
              >
                <option value="distance">Distance</option>
                <option value="alphabetical">Alphabetical</option>
              </select>
            </div>

            {activeTab === 'band' && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-dark)', display: 'block', marginBottom: '8px' }}>
                  Genre {selectedGenres.length > 0 && <span style={{ color: 'var(--accent-primary)' }}>({selectedGenres.length})</span>}
                </label>
                <div className="filter-checkboxes" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', maxHeight: '200px', overflowY: 'auto' }}>
                  {GENRE_LIST.map(g => (
                    <label key={g} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: 'var(--text-dark)' }}>
                      <input type="checkbox" checked={selectedGenres.includes(g)} onChange={() => handleGenreToggle(g)} />
                      {g}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'venue' && (
              <>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-dark)', display: 'block', marginBottom: '8px' }}>
                    Venue Type {selectedVenueTypes.length > 0 && <span style={{ color: 'var(--accent-primary)' }}>({selectedVenueTypes.length})</span>}
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {VENUE_TYPES.map(t => (
                      <label key={t} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: 'var(--text-dark)' }}>
                        <input type="checkbox" checked={selectedVenueTypes.includes(t)} onChange={() => handleVenueTypeToggle(t)} />
                        {t.charAt(0).toUpperCase() + t.slice(1)}
                      </label>
                    ))}
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-dark)', display: 'block', marginBottom: '8px' }}>Age Restriction</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {[{ v: 'all_ages', l: 'All Ages' }, { v: '18_plus', l: '18+' }, { v: '21_plus', l: '21+' }].map(({ v, l }) => (
                      <label key={v} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: 'var(--text-dark)' }}>
                        <input type="checkbox" checked={selectedAges.includes(v)} onChange={() => handleAgeToggle(v)} />
                        {l}
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        <div style={{ padding: '0 16px', marginBottom: '12px', color: 'var(--text-light)', fontSize: '14px' }}>
          {loading ? 'Loading...' : `${sortedUsers.length} account${sortedUsers.length !== 1 ? 's' : ''} found`}
        </div>

        {error ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--accent-danger)' }}>{error}</div>
        ) : (
          <div className="results-grid">
            {sortedUsers.map(user => (
              <div
                key={user.id}
                onClick={() => navigate(`/users/${user.id}`)}
                style={{
                  background: 'var(--coal-black)', 
                  borderRadius: '8px', 
                  cursor: 'pointer',
                  transition: 'all 0.2s ease', 
                  border: '1px solid var(--border-dark)',
                  display: 'flex', 
                  flexDirection: 'column',
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--accent-primary)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-dark)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {/* Full-width square profile image */}
                <div style={{ 
                  width: '100%', 
                  aspectRatio: '1 / 1',
                  backgroundColor: 'var(--smoke-gray)', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  overflow: 'hidden'
                }}>
                  {user.profile_image_url ? (
                    <img src={user.profile_image_url} alt={user.display_name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => { e.target.style.display = 'none'; }} />
                  ) : (
                    <span style={{ fontSize: '70px', color: 'var(--text-light)' }}>
                      {user.display_name?.[0]?.toUpperCase() || '?'}
                    </span>
                  )}
                </div>

                {/* Text section */}
                <div style={{ padding: '16px', textAlign: 'center' }}>
                  <div className="user-card-name" style={{ fontWeight: '700', fontSize: '16px', marginBottom: '6px', color: 'var(--text-light)' }}>
                    {user.display_name}
                  </div>
                  {getCardSubtitle(user) && (
                    <div className="user-card-subtitle" style={{ fontSize: '13px', color: '#aaa', lineHeight: 1.5 }}>
                      {getCardSubtitle(user)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default FindPage;
